import {Box} from 'quickstart/components/layout/Box'
import {Grid} from 'quickstart/components/layout/Grid'
import {SearchForm} from 'quickstart/components/tizra/SearchForm'
import {ConnectedField} from 'quickstart/components/controls/ConnectedField'
import {Select} from 'quickstart/components/controls/Select'
import {useHitCounts} from 'quickstart/hooks'
import {UseSearchReturn} from 'quickstart/hooks/useSearch'
import {
  asOptions,
  formFieldKey,
  formFields,
  SearchField,
} from 'quickstart/lib/search/config'
import {truish} from 'quickstart/types'
import {useMemo} from 'react'
import {logger} from 'tizra'

const log = logger('quickstart/components/tizra/Browse/BrowseFilters')

// Classic search with Semantic uses text/value but Quickstart components use
// label/value. We need to remap because the config is still based on the old
// nomenclature.
const renameTextToLabel = <T extends {label?: string; text?: string}>({
  label,
  text,
  ...rest
}: T): Omit<T, 'text' | 'label'> & {label: string} => ({
  label: label || text || '',
  ...rest,
})

type BrowseFiltersConfigFieldProps = {
  field: SearchField<string> & {allOption?: {text: string; value: string}}
  search: UseSearchReturn
}

type SearchFieldWithRenamedOptions = Omit<SearchField<string>, 'options'> & {
  options: Array<{label: string; value: string}>
}

const BrowseFiltersConfigField = ({
  field: _field,
  search,
}: BrowseFiltersConfigFieldProps) => {
  const hitCounts = useHitCounts({field: _field, search})

  const field = useMemo<SearchFieldWithRenamedOptions>(() => {
    const {allOption = {text: 'All', value: ''}, options: _options} = _field
    const options = asOptions(_options)
    return {
      ..._field,
      options: [
        !options.some(
          o => o.text === allOption.text || o.value === allOption.value,
        ) && allOption,
        ...options.filter(({value}) => !hitCounts || hitCounts[value]),
      ]
        .filter(truish)
        .map(renameTextToLabel),
    }
  }, [_field, hitCounts])

  return (
    <ConnectedField
      // @ts-expect-error
      name={field.name}
      component={Select}
      options={field.options}
      placeholder={field.placeholder || field.label}
    />
  )
}

type BrowseFiltersProps = {
  search: UseSearchReturn
}

export const BrowseFilters = ({
  search,
  search: {config},
}: BrowseFiltersProps) => {
  // Memoize so that fields/ffks don't change on every render, causing looping
  // renders via effects.
  const [fields, ffks] = useMemo(() => {
    const fields = formFields<string>(config, 'browseFilters')
    return [fields, fields.map(formFieldKey)]
  }, [config])

  return (
    // Note: breakpoints below for cols and width must match
    <SearchForm instant search={search}>
      <Grid cols={{md: fields.length}}>
        {fields.map((field, i) => (
          <Box
            key={ffks[i]}
            width={{xs: '100%', md: '16rem'}}
            justifySelf={
              fields.length === 1 ? 'center' : i === 0 ? 'end' : 'start'
            }
          >
            <BrowseFiltersConfigField field={field} search={search} />
          </Box>
        ))}
      </Grid>
    </SearchForm>
  )
}
